import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Clock, Check, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { formatPrice } from "../lib/api";

export const AccentTitle = ({ text, className = "", accentClass = "text-jannah-green", highlights = {} }) => {
  const { lang } = useLang();
  const words = String(text || "").trim().split(" ");
  const hl = (w, i) => {
    const key = w.replace(/[^\p{L}\p{N}]/gu, "");
    const cls = highlights[key];
    return cls ? <span key={i} className={cls}>{w}</span> : <React.Fragment key={i}>{w}</React.Fragment>;
  };
  const join = (arr) => arr.flatMap((w, i) => (i === 0 ? [hl(w, i)] : [" ", hl(w, i)]));
  if (lang !== "en" || words.length < 2) return <span className={className}>{join(words)}</span>;
  const last = words.pop();
  return (
    <span className={className}>
      {join(words)} <em className={`serif-accent ${accentClass}`}>{last}</em>
    </span>
  );
};

export const SectionHeader = ({ eyebrow, title, subtitle, center = true, light = false }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : ""} mb-12`}>
    <span className={`eyebrow ${center ? "" : "eyebrow-line"} ${light ? "text-jannah-gold-light" : "text-jannah-gold"}`}>
      {eyebrow}
    </span>
    <h2 className={`mt-4 text-4xl md:text-5xl font-bold tracking-tight ${light ? "text-white" : "text-jannah-navy"}`}>
      <AccentTitle text={title} accentClass={light ? "text-jannah-gold-light" : "text-jannah-green"} />
    </h2>
    {subtitle && (
      <p className={`mt-5 text-base md:text-lg leading-relaxed ${light ? "text-white/75" : "text-muted-foreground"}`}>
        {subtitle}
      </p>
    )}
  </div>
);

export const PageBanner = ({ title, subtitle, image }) => {
  const { IMAGES } = useContent();
  return (
  <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${image || IMAGES.pageBanner})` }}
    />
    <div className="absolute inset-0 hero-overlay" />
    <div className="absolute inset-0 pattern-overlay opacity-30" />
    <div className="relative max-w-7xl mx-auto px-6 text-center">
      <div className="gold-divider mx-auto mb-5" />
      <h1 className="text-4xl md:text-6xl font-bold text-white animate-fade-up">{title}</h1>
      {subtitle && (
        <p className="mt-5 max-w-2xl mx-auto text-white/80 text-lg leading-relaxed">{subtitle}</p>
      )}
    </div>
  </section>
  );
};

export const PackageCard = ({ pkg }) => {
  const { t, lang } = useLang();
  const name = lang === "en" ? pkg.name_en : pkg.name_bn;
  const tagline = lang === "en" ? pkg.tagline_en : pkg.tagline_bn;
  const includes = lang === "en" ? pkg.includes_en : pkg.includes_bn;

  return (
    <div className="group gold-tracer card-hover bg-white rounded-3xl overflow-hidden border border-jannah-sand flex flex-col h-full shadow-ambient" data-testid={`package-card-${pkg.id}`}>
      <div className="relative h-60 overflow-hidden">
        <img src={pkg.image} alt={name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-jannah-obsidian/85 via-jannah-green-dark/20 to-transparent" />
        <div className="absolute top-4 left-4 flex gap-2">
          {pkg.featured && (
            <span className="px-3 py-1 rounded-full btn-gold text-[11px] font-bold uppercase tracking-wider">
              {lang === "en" ? "Most Popular" : "জনপ্রিয়"}
            </span>
          )}
          <span className="px-3 py-1 rounded-full glass text-jannah-green-dark text-[11px] font-bold uppercase tracking-wider">{pkg.tier}</span>
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          <div className="glass-dark rounded-2xl px-4 py-2.5">
            <div className="text-[10px] uppercase tracking-widest text-white/70">{t.from}</div>
            <div className="text-2xl font-bold text-jannah-gold-light leading-tight">{formatPrice(pkg)}</div>
          </div>
          <div className="flex items-center gap-1 text-white text-sm glass-dark rounded-full px-3 py-1.5">
            <Star size={14} className="fill-jannah-gold text-jannah-gold" />
            <span className="font-semibold">{pkg.rating.toFixed(1)}</span>
          </div>
        </div>
      </div>

      <div className="p-7 flex flex-col flex-1">
        <h3 className="text-2xl font-bold text-jannah-navy tracking-tight">{name}</h3>
        <p className="text-sm text-muted-foreground mt-1.5">{tagline}</p>

        <div className="mt-5 flex flex-wrap gap-2 text-xs font-medium">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-jannah-green/10 text-jannah-green-dark"><Clock size={13} className="text-jannah-gold" /> {pkg.duration} {t.days}</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-jannah-green/10 text-jannah-green-dark"><MapPin size={13} className="text-jannah-gold" /> {pkg.makkah_dist}</span>
        </div>

        <ul className="mt-5 space-y-2 text-sm">
          {includes.slice(0, 4).map((inc, i) => (
            <li key={i} className="flex items-start gap-2.5 text-jannah-ink/70">
              <span className="mt-0.5 w-4 h-4 rounded-full bg-jannah-green/10 flex items-center justify-center shrink-0"><Check size={10} className="text-jannah-green" /></span> {inc}
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-6">
          <Link
            to={`/package/${pkg.slug}`}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-jannah-green-dark text-white text-sm font-semibold hover:bg-jannah-green transition-colors"
            data-testid={`package-card-link-${pkg.id}`}
          >
            {t.view_details} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};
